import moment from 'moment';
import * as React from 'react';

import { connect } from 'react-redux';

import { API_URL } from '../../config';
import { localeData } from '../../locales/data';
import { StateType } from '../../reducers';
import { getUserDataSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';

import { FilesListProps } from './types';
import { FieldSet } from './FieldSet';
import { PreviewConnect } from './ImagePreview';
import { useEffect, useRef } from 'react';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';

const FilesList = ({ data }: FilesListProps) => {
  if (data && data.getIn(['resume', 'dateCreated'])) {
    return (
      <ul className="account__files-upload-list">
        <li className="account__files-upload">
          <FormattedMessageHelper tagName="span" id="step1.last-updated" defaultMessage={localeData.en['step1.last-updated']} />
          {moment(data.getIn(['resume', 'dateCreated'])).format('L LT')}
          <a className="account__files-upload-link" href={`${API_URL}/users/${data.get('id')}/resume`} download="resume" target="blank">
            <FormattedMessageHelper tagName="span" id="step1.download" defaultMessage={localeData.en['step1.download']} />
          </a>
        </li>
      </ul>
    );
  } else {
    return null;
  }
};

const mapStateToProps = (state: StateType): FilesListProps => ({
  data: getUserDataSelector(state),
});

const FilesListConnect = connect<FilesListProps, {}, any, StateType>(mapStateToProps, {})(FilesList);

interface FileInputProps {
  label?: string;
  required?: boolean;
  modifiers?: string[];
  acceptFiles: string;
  handlerChange: (files: File[]) => void;
  handleRemove: () => void;
  id: string;
  files: FileList | null;
  name: string;
  tabIndex: number;
  errors?: string[];
  showFileList?: boolean;
  showPreview: boolean;
  hintId?: string;
}

export type InputFileType = 'photo' | 'resume';

interface InputFileProps {
  label?: string;
  required?: boolean;
  modifiers?: string[];
  acceptFiles: string;
  handlerPhotoFile: (files: File[] | null, errors: string[]) => void;
  handlerResumeFile: (files: File[] | null, errors: string[]) => void;
  id: string;
  files: FileList | null;
  name: string;
  type: InputFileType;
  tabIndex: number;
  fileErrors?: string[];
  showFileList?: boolean;
  showPreview?: boolean;
  hintId?: string;
}

interface InputFileState {
  errors: string[];
  files: any[];
}

const FileInput = ({
  label,
  required = false,
  modifiers,
  acceptFiles,
  handlerChange,
  handleRemove,
  files,
  tabIndex,
  id,
  name,
  errors = [],
  showFileList = true,
  showPreview,
  hintId = 'step1.files.upload.accepted',
}: FileInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handlerChange(Array.from(event.currentTarget.files || []));
  };

  //remove file from file input on removing upload, so that the same file could be selected again
  useEffect(() => {
    if ((!files || files.length === 0) && fileInputRef && fileInputRef.current && fileInputRef.current.value) {
      fileInputRef.current.value = '';
    }
  }, [files]);

  return (
    <FieldSet id={id} label={label} required={required} modifiers={modifiers}>
      <React.Fragment>
        {showPreview && <PreviewConnect files={files} onRemoveFile={handleRemove} onUpdateFile={handlerChange} />}
        <div className="input-file">
          <input
            ref={fileInputRef}
            tabIndex={tabIndex}
            type="file"
            id={id}
            name={name}
            accept={acceptFiles}
            onChange={handleFileChange}
            className="input-file__none"
          />
          <label htmlFor={id} className="input-file__button">
            <FormattedMessageHelper tagName="span" id="input.file.browse" defaultMessage="Browse" />
          </label>
          <div className="input-file__label">
            {files && files.length !== 0 ? (
              files[0].name
            ) : (
              <FormattedMessageHelper tagName="span" id="input.file.none" defaultMessage={localeData.en['input.file.none']} />
            )}
          </div>
        </div>
        <div className="input__error">
          {errors && errors.length !== 0 && (
            <div className="error">
              <FormattedMessageHelper tagName="span" id={errors[0]} defaultMessage={localeData.en[errors[0]]} />
            </div>
          )}
        </div>
        {showFileList && <FilesListConnect />}
        <div className="input-file__info_small">
          <FormattedMessageHelper tagName="span" id={hintId} defaultMessage={localeData.en[hintId]} />
          <br />
          <FormattedMessageHelper tagName="span" id="step1.file.upload.size" defaultMessage={localeData.en['step1.file.supload.size']} />
        </div>
      </React.Fragment>
    </FieldSet>
  );
};

class InputFile extends React.Component<InputFileProps, InputFileState> {
  state = { errors: [], files: [] };
  fileHandler = this.props.type === 'photo' ? this.props.handlerPhotoFile : this.props.handlerResumeFile;

  handlerChange = (files: File[]) => {
    if (files && files.length) {
      const errors = this.validation(files[0]);
      this.fileHandler(files, errors);
    } else {
      this.fileHandler([], []);
    }
  };

  handleRemove = () => {
    this.fileHandler(null, []);
  };

  handleChangeFile = (file: File) => {
    const errors = this.validation(file);
    this.fileHandler([file], errors);
  };

  validationAcceptFiles(file: File): string[] {
    let errors: string[] = [];
    const acceptFiles: any = this.props.acceptFiles.split(',');
    if (acceptFiles.includes(file.type) === false) {
      errors = ['upload.files.not.accept'];
    }

    return errors;
  }

  validationFileSize(file: any): string[] {
    let errors: string[] = [];

    if (file.size > 5242880) {
      errors = ['upload.files.size.large'];
    }

    return errors;
  }

  validation(file: any) {
    return [...this.validationAcceptFiles(file), ...this.validationFileSize(file)];
  }

  render() {
    const {
      label,
      required = false,
      modifiers,
      acceptFiles,
      tabIndex,
      id,
      name,
      showFileList = true,
      showPreview = false,
      fileErrors = [],
      files = null,
      hintId,
    } = this.props;

    return (
      <FileInput
        label={label}
        required={required}
        modifiers={modifiers}
        acceptFiles={acceptFiles}
        handlerChange={this.handlerChange}
        handleRemove={this.handleRemove}
        files={files}
        tabIndex={tabIndex}
        hintId={hintId}
        id={id}
        name={name}
        showFileList={showFileList}
        showPreview={showPreview}
        errors={fileErrors}
      />
    );
  }
}

export default InputFile;
